import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigationStore } from '@/store/navigation.store';
import { useGlobalDateRange } from '@/store/date-filter.store';
import { amlService } from '@/services/aml.service';
import type { AMLIssueType, AMLIssueStatus } from '@/types/aml.types';

// Menu issue type includes 'all' for summary
export type AMLMenuIssueType = AMLIssueType | 'all';

interface AMLContextType {
  // Count data for navigation menu
  counts: Record<string, number>;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // Count data for navigation menu (matching ComplianceContext interface)
  getMenuCount: (issueType: AMLMenuIssueType) => number;
  hasMenuIssues: (issueType: AMLMenuIssueType) => boolean;
  totalMenuIssues: number;

  // Legacy alias for backward compatibility
  getAMLMenuCount: (issueType: AMLMenuIssueType) => number;
  hasData: boolean;

  // Data management
  loadAMLData: () => Promise<void>;
  refreshData: () => Promise<void>;
  clearError: () => void;

  // Cache status
  isDataStale: boolean;
  cacheExpiresAt: Date | null;
}

interface AMLProviderProps {
  children: React.ReactNode;
  cacheTimeoutMs?: number;
}

const AMLContext = createContext<AMLContextType | null>(null);

// Cache timeout (2 minutes) - matches ComplianceContext
const DEFAULT_CACHE_TIMEOUT = 2 * 60 * 1000;

// Active statuses for sidebar counts (exclude closed)
const ACTIVE_AML_STATUSES: AMLIssueStatus[] = ['new', 'second_review', 'needs_info'];

/**
 * AMLProvider - Single source of truth for AML data
 * Provides AML data to both sidebar navigation and AML pages
 * Handles caching, loading states, and filtering in one place
 *
 * Follows ComplianceContext pattern exactly for consistency
 */
export function AMLProvider({
  children,
  cacheTimeoutMs = DEFAULT_CACHE_TIMEOUT
}: AMLProviderProps) {
  // Global state dependencies
  const selectedClientId = useNavigationStore((state) => state.selectedClientId);
  const selectedEntityId = useNavigationStore((state) => state.selectedEntityId);
  const entities = useNavigationStore((state) => state.entities);
  const loadEntities = useNavigationStore((state) => state.loadEntities);
  const { dateRange, getApiParams } = useGlobalDateRange();

  // Sidebar counts ALWAYS use active statuses (new, second_review, needs_info)
  // This ensures consistent "action needed" counts regardless of detail page filters
  // Note: Removed localStorage sync - sidebar counts should not change based on user's filter selection

  // Internal state
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [cacheExpiresAt, setCacheExpiresAt] = useState<Date | null>(null);

  // Track what entity/client combination AND date range the cached data is for
  // Note: Status filters removed - sidebar always uses ACTIVE_AML_STATUSES for consistent "action needed" counts
  const [cachedEntityContext, setCachedEntityContext] = useState<{
    clientId: string | null;
    entityId: string | 'all' | null;
    dateRange: { start?: string; end?: string } | null;
  }>({ clientId: null, entityId: null, dateRange: null });

  // Request deduplication - prevent multiple simultaneous API calls
  const loadingPromiseRef = useRef<{ promise: Promise<void>; controller: AbortController } | null>(null);

  // Check if data is stale (cache expired or entity/date context changed)
  const isDataStale = useMemo(() => {
    if (!cacheExpiresAt || !lastUpdated) return true;

    // Check if entity context has changed
    const entityContextChanged =
      cachedEntityContext.clientId !== selectedClientId ||
      cachedEntityContext.entityId !== selectedEntityId;

    // Check if date range has changed
    const dateRangeChanged =
      JSON.stringify(cachedEntityContext.dateRange) !== JSON.stringify(dateRange);

    // Stale if time expired OR entity context changed OR date range changed
    return new Date() > cacheExpiresAt || entityContextChanged || dateRangeChanged;
  }, [cacheExpiresAt, lastUpdated, cachedEntityContext, selectedClientId, selectedEntityId, dateRange]);

  // Load AML data function with request deduplication
  const loadAMLData = useCallback(async () => {
    if (!selectedClientId) {
      console.warn('[AMLContext] No client selected, cannot load AML data');
      setCounts({});
      setError(null);
      setLastUpdated(null);
      setCacheExpiresAt(null);
      setCachedEntityContext({ clientId: null, entityId: null, dateRange: null });
      return;
    }

    // Request deduplication: if there's already a loading promise, return it
    if (loadingPromiseRef.current) {
      return loadingPromiseRef.current.promise;
    }

    // Create AbortController for this request
    const controller = new AbortController();

    // Create the actual loading promise
    const loadingPromise = (async () => {
      setIsLoading(true);
      setError(null);

      try {

        // Get date filter parameters for server-side filtering
        const dateParams = getApiParams();

        if (selectedEntityId && selectedEntityId !== 'all') {
          // Load counts for specific entity using fast counts endpoint

          try {
            const countsResponse = await amlService.getCounts(selectedEntityId, {
              status: ACTIVE_AML_STATUSES,  // Always use active statuses for sidebar counts
              dateFrom: dateParams.date_from,
              dateTo: dateParams.date_to,
              signal: controller.signal
            });


            // Build counts map matching frontend expectations (K-U all AML types)
            const countsMap: Record<string, number> = {
              all: countsResponse.action_needed || 0,
              K: 0, L: 0, M: 0, N: 0, O: 0, P: 0, Q: 0, R: 0, S: 0, T: 0, U: 0,
              ...countsResponse.issues_by_type
            };

            setCounts(countsMap);
          } catch (countsError) {
            console.warn('[AMLContext] Fast counts failed, falling back to empty counts:', countsError);
            setCounts({});
          }
        } else {
          // For "all entities" or no specific entity, we need to aggregate across entities

          try {
            let clientEntities = entities;

            if (clientEntities.length === 0) {
              await loadEntities(selectedClientId);
              clientEntities = useNavigationStore.getState().entities;
            }

            if (clientEntities.length === 0) {
              console.warn('[AMLContext] No entities found for client, returning empty counts');
              setCounts({});
            } else {

              // Load fast counts for each entity in parallel
              const countsPromises = clientEntities.map(entity =>
                amlService.getCounts(entity.entityId, {
                  status: ACTIVE_AML_STATUSES,  // Always use active statuses for sidebar counts
                  dateFrom: dateParams.date_from,
                  dateTo: dateParams.date_to,
                  signal: controller.signal
                }).catch(entityError => {
                  console.warn(`[AMLContext] Failed to load counts for entity ${entity.entityId}:`, entityError);
                  return {
                    entity_id: entity.entityId,
                    total_issues: 0,
                    issues_by_type: {},
                    issues_by_status: {},
                    action_needed: 0,
                    date_range: { from: null, to: null },
                    status_filters: [],
                    processing_time_ms: 0
                  };
                })
              );

              const countsResponses = await Promise.all(countsPromises);

              // Aggregate counts from all entities (K-U all AML types)
              const aggregatedCounts: Record<string, number> = {
                all: 0, K: 0, L: 0, M: 0, N: 0, O: 0, P: 0, Q: 0, R: 0, S: 0, T: 0, U: 0
              };

              countsResponses.forEach((countsResponse) => {
                aggregatedCounts.all += countsResponse.action_needed || 0;
                if (countsResponse.issues_by_type) {
                  Object.entries(countsResponse.issues_by_type).forEach(([type, count]) => {
                    aggregatedCounts[type] = (aggregatedCounts[type] || 0) + (count as number);
                  });
                }
              });

              setCounts(aggregatedCounts);
            }
          } catch (aggregationError) {
            console.error('[AMLContext] Entity summary aggregation failed:', aggregationError);
            setCounts({});
          }
        }

        setLastUpdated(new Date());
        setCacheExpiresAt(new Date(Date.now() + cacheTimeoutMs));

        // Update cached entity context to track what data we have cached
        setCachedEntityContext({
          clientId: selectedClientId,
          entityId: selectedEntityId,
          dateRange: dateRange
        });

      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Don't update state for aborted requests
        }
        const errorMessage = err instanceof Error ? err.message : 'Failed to load AML data';
        console.error('[AMLContext] Error loading AML data:', err);
        setError(errorMessage);
        setCounts({});
        setLastUpdated(null);
        setCacheExpiresAt(null);
        setCachedEntityContext({ clientId: null, entityId: null, dateRange: null });
      } finally {
        setIsLoading(false);
      }
    })();

    // Store the promise and controller for deduplication and potential cancellation
    loadingPromiseRef.current = { promise: loadingPromise, controller };

    try {
      await loadingPromise;
    } finally {
      // Clear the promise reference when done (success or failure)
      loadingPromiseRef.current = null;
    }
  }, [selectedClientId, selectedEntityId, cacheTimeoutMs, getApiParams, dateRange, entities, loadEntities]);

  // Refresh data (force reload)
  const refreshData = useCallback(async () => {
    // Abort any existing loading request and clear reference
    if (loadingPromiseRef.current) {
      loadingPromiseRef.current.controller.abort('Force refresh requested');
      loadingPromiseRef.current = null;
    }
    setCacheExpiresAt(null); // Force cache expiry
    await loadAMLData();
  }, [loadAMLData]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Stable reference to loadAMLData to prevent infinite loops
  const loadAMLDataRef = useRef(loadAMLData);
  loadAMLDataRef.current = loadAMLData;

  // Auto-load data when client/entity selection changes OR date range changes OR status filters change
  useEffect(() => {
    if (!selectedClientId) {
      setCounts({});
      setError(null);
      setLastUpdated(null);
      setCacheExpiresAt(null);
      setCachedEntityContext({ clientId: null, entityId: null, dateRange: null });
    } else if (isDataStale) {
      loadAMLData();
    }
  }, [selectedClientId, selectedEntityId, dateRange, isDataStale, loadAMLData]);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cancel any pending requests
      if (loadingPromiseRef.current) {
        loadingPromiseRef.current.controller.abort('Component unmounted');
        loadingPromiseRef.current = null;
      }
      setCounts({});
      setError(null);
      setLastUpdated(null);
      setCacheExpiresAt(null);
    };
  }, []);

  // Get count for menu item (matching ComplianceContext interface)
  const getMenuCount = useCallback((issueType: AMLMenuIssueType): number => {
    return counts[issueType] || 0;
  }, [counts]);

  // Check if issue type has any issues
  const hasMenuIssues = useCallback((issueType: AMLMenuIssueType): boolean => {
    return getMenuCount(issueType) > 0;
  }, [getMenuCount]);

  // Total issues count
  const totalMenuIssues = useMemo(() => {
    return counts['all'] || 0;
  }, [counts]);

  // Check if we have data
  const hasData = useMemo(() => {
    return Object.keys(counts).length > 0 && lastUpdated !== null;
  }, [counts, lastUpdated]);

  // Context value (matching ComplianceContext structure)
  const contextValue: AMLContextType = useMemo(() => ({
    // Data state
    counts,
    isLoading,
    error,
    lastUpdated,

    // Count data for navigation menu (matching ComplianceContext interface)
    getMenuCount,
    hasMenuIssues,
    totalMenuIssues,

    // Legacy alias for backward compatibility
    getAMLMenuCount: getMenuCount,
    hasData,

    // Data management
    loadAMLData,
    refreshData,
    clearError,

    // Cache status
    isDataStale,
    cacheExpiresAt
  }), [counts, isLoading, error, lastUpdated, getMenuCount, hasMenuIssues, totalMenuIssues, hasData, loadAMLData, refreshData, clearError, isDataStale, cacheExpiresAt]);

  return (
    <AMLContext.Provider value={contextValue}>
      {children}
    </AMLContext.Provider>
  );
}

/**
 * Hook to access AML context
 * Provides access to shared AML data and menu counts
 */
export function useAMLContext(): AMLContextType {
  const context = useContext(AMLContext);
  if (!context) {
    throw new Error('useAMLContext must be used within an AMLProvider');
  }
  return context;
}

/**
 * Hook for navigation menu - only provides count data without loading AML data
 * This is used by the sidebar to show counts when data is available, without forcing data loads
 */
export function useAMLMenuCounts() {
  const context = useContext(AMLContext);

  // If no context provider, return empty counts (graceful degradation)
  if (!context) {
    console.warn('useAMLMenuCounts called outside AMLProvider - returning empty counts');
    return {
      getMenuCount: () => 0,
      hasMenuIssues: () => false,
      totalMenuIssues: 0,
      hasData: false,
      isLoading: false,
      // Legacy aliases
      getAMLMenuCount: () => 0
    };
  }

  return {
    getMenuCount: context.getMenuCount,
    hasMenuIssues: context.hasMenuIssues,
    totalMenuIssues: context.totalMenuIssues,
    hasData: context.hasData,
    isLoading: context.isLoading,
    // Legacy aliases
    getAMLMenuCount: context.getAMLMenuCount
  };
}
